import { caseworkCentroid } from "building-generation/elements/caseworks/casework";
import type { Casework } from "building-types/casework/caseworkTypes";
import type { BuildingViewerViewMode } from "building-types/geometry/buildingGeometryTypes";
import { BuildingGeometryMode, type BuildingGeometryEmitter } from "building-types/geometry/buildingGeometryTypes";
import { roomEntityHandleForSelectingReportingUnit } from "building-types/handles/roomEntityHandleForSelectingReportingUnit";
import type { LevelExtrusion } from "building-types/levels/levelPerimeters";
import type { Level } from "building-types/levels/levelTypes";
import type { ExteriorPalette } from "building-types/materials/exteriorPaletteTypes";
import { FaceCategory } from "building-types/materials/faceCategories";
import type { InteriorPalette } from "building-types/materials/interiorPaletteTypes";
import { PaletteType } from "building-types/materials/paletteType";
import type { Table } from "common/data/table";
import { materialSrcFromPalette } from "doc/materialLookup";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { brepAABB3, extrusionAABB3 } from "geom/brep";
import { extrusionToBRep } from "geom/extrude";
import { UVGeneration } from "geom/uv";
import centroidInsideLevelExtrusions from "../utils/centroidInsideLevelExtrusions";
import { buildInteriorLayers, buildExteriorLayers } from "./buildingGeometryTools";

type CaseworkGeometryBuildingFields = {
  casework: Table<Casework>;
  interiorPalette: InteriorPalette;
  exteriorPalette: ExteriorPalette;
  levelExtrusions: Table<LevelExtrusion>;
  levels: Table<Level>;
  viewMode?: BuildingViewerViewMode;
};

/**
 * Classifies casework as interior or exterior for lighting and clipping purposes.
 *
 * When the centroid is unusable, fall back to the room the casework belongs to rather than letting
 * a degenerate sub-geometry light the whole piece as exterior.
 */
function caseworkIsInsideBuilding(casework: Casework, levelExtrusions: Table<LevelExtrusion>): boolean {
  return (
    centroidInsideLevelExtrusions(levelExtrusions, caseworkCentroid(casework)) ??
    isInteriorRoomEnclosure[casework.room.attributes.roomEnclosure] === true
  );
}

function caseworkExtendsBelowElevation(casework: Casework, elevationThreshold: number): boolean {
  for (const extrusion of casework.wallSweepExtrusions) {
    const aabb = extrusionAABB3(extrusion);
    if (aabb.minZ < elevationThreshold) {
      return true;
    }
  }
  for (const brep of casework.breps) {
    const aabb = brepAABB3(brep);
    if (aabb.minZ < elevationThreshold) {
      return true;
    }
  }
  return false;
}

export function getCaseworkGeometries(
  building: CaseworkGeometryBuildingFields,
  mode: BuildingGeometryMode,
  emitter: BuildingGeometryEmitter,
): void {
  const { interiorPalette, exteriorPalette } = building;
  const includeExtraCategories = mode === BuildingGeometryMode.BUILDING_VIEWER || mode === BuildingGeometryMode.EXPORT;
  for (const caseId in building.casework) {
    const casework = building.casework[caseId];
    emitter.beginGroup?.("Casework", caseId);

    const isInsideBuilding = caseworkIsInsideBuilding(casework, building.levelExtrusions);

    const threeLayers = isInsideBuilding ? buildInteriorLayers() : buildExteriorLayers();

    const palette = casework.paletteType === PaletteType.INTERIOR_PALETTE ? interiorPalette : exteriorPalette;
    const material = materialSrcFromPalette(palette, casework.material, casework.room.type);

    const extrusions = [...casework.decorativeExtrusions, ...casework.wallSweepExtrusions];

    const entityHandleForSelectingReportingUnit = roomEntityHandleForSelectingReportingUnit(casework.room);

    // If the casework extends significantly below the floor elevation, don't associate it with a level.
    // Casework has been used as a generic modeling tool, so we want large pieces of casework to be visible even if their level is hidden.
    const elevationThreshold = building.levels[casework.room.level.id].floorElevation - 2;
    const associateCaseworkWithLevel = !caseworkExtendsBelowElevation(casework, elevationThreshold);

    const commonProps = {
      faceCategory: FaceCategory.CASEWORK,
      materialSlot: casework.material,
      materialSrc: material,
      uvGen: UVGeneration.GLOBAL_RECT_UV,
      entityId: casework.id,
      entityHandleForSelectingReportingUnit,
      pickHandle: casework.pickHandle,
      threeLayers,
      levelId: associateCaseworkWithLevel ? casework.room.level.id : undefined,
    };

    for (const extrusion of extrusions) {
      emitter.emit({ ...commonProps, geom: extrusionToBRep(extrusion) });
    }

    const breps = includeExtraCategories ? [...casework.breps, ...casework.decorativeBReps] : casework.breps;

    for (const brep of breps) {
      emitter.emit({ ...commonProps, geom: brep });
    }

    if (includeExtraCategories) {
      const countertopMaterial = materialSrcFromPalette(palette, casework.countertopMaterial, casework.room.type);
      for (const extrusion of casework.countertopExtrusions) {
        emitter.emit({
          ...commonProps,
          geom: extrusionToBRep(extrusion),
          materialSlot: casework.countertopMaterial,
          materialSrc: countertopMaterial,
        });
      }
    }

    emitter.endGroup?.("Casework");
  }
}
